import { useEffect, useMemo, useRef, useState } from "react";
import {
  addMonths,
  format,
  isAfter,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { DayPicker, DayFlag, UI } from "react-day-picker";

const dayPickerClassNames = {
  [UI.Root]: "w-full",
  [UI.Months]: "w-full",
  [UI.Month]: "w-full px-3 pb-3 pt-2",
  [UI.MonthCaption]: "hidden",
  [UI.CaptionLabel]: "hidden",
  [UI.Nav]: "hidden",
  [UI.PreviousMonthButton]: "hidden",
  [UI.NextMonthButton]: "hidden",
  [UI.Chevron]: "h-0 w-0",
  [UI.MonthGrid]: "w-full table-fixed border-collapse",
  [UI.Weekdays]: "border-0",
  [UI.Weekday]:
    "pb-2 text-center text-[10.5px] font-semibold uppercase tracking-[0.06em] text-(--text-muted)",
  [UI.Week]: "h-8",
  [UI.Day]: "h-8 p-0 text-center align-middle",
  [UI.DayButton]:
    "mx-auto flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent p-0 text-[12.5px] font-medium text-(--text-primary) transition hover:bg-(--surface-2)",
  [DayFlag.outside]: "[&>button]:text-(--text-faint)",
  [DayFlag.today]: "[&>button]:font-bold [&>button]:text-(--brand-primary)",
};

const modifierClassNames = {
  selected_single:
    "[&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
  selected_start:
    "rounded-l-md bg-(--pastel-violet) [&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
  selected_middle: "bg-(--pastel-violet) [&>button]:text-(--text-primary)",
  selected_end:
    "rounded-r-md bg-(--pastel-violet) [&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
};

const parse = (value) => {
  if (!value) return null;
  try {
    const d = parseISO(value);
    return Number.isNaN(d.getTime()) ? null : startOfDay(d);
  } catch {
    return null;
  }
};

const toValue = (date) => (date ? format(date, "yyyy-MM-dd") : "");

const triggerLabel = (from, to) => {
  if (!from && !to) return null;
  if (from && to) {
    if (isSameDay(from, to)) return format(from, "MMM d, yyyy");
    const sameYear = from.getFullYear() === to.getFullYear();
    return sameYear
      ? `${format(from, "MMM d")} – ${format(to, "MMM d, yyyy")}`
      : `${format(from, "MMM d, yyyy")} – ${format(to, "MMM d, yyyy")}`;
  }
  if (from) return `${format(from, "MMM d, yyyy")} → …`;
  return `… → ${format(to, "MMM d, yyyy")}`;
};

export default function FlightDateRange({
  startDate,
  endDate,
  onChange,
  placeholder = "Select flight dates",
}) {
  const from = useMemo(() => parse(startDate), [startDate]);
  const to = useMemo(() => parse(endDate), [endDate]);

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState(
    startOfMonth(from ?? to ?? new Date()),
  );
  const rootRef = useRef(null);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setVisibleMonth(startOfMonth(from ?? to ?? new Date()));
      setAnchor(null);
    }
    prevOpenRef.current = open;
  }, [open, from, to]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const handleDayClick = (day) => {
    const clicked = startOfDay(day);
    if (anchor === null) {
      setAnchor(clicked);
      onChange(toValue(clicked), toValue(clicked));
      return;
    }
    const next = isAfter(anchor, clicked)
      ? { from: clicked, to: anchor }
      : { from: anchor, to: clicked };
    onChange(toValue(next.from), toValue(next.to));
    setAnchor(null);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("", "");
    setAnchor(null);
  };

  const label = triggerLabel(from, to);

  const modifiers =
    from && to
      ? isSameDay(from, to)
        ? { selected_single: from }
        : {
            selected_start: from,
            selected_middle: { after: from, before: to },
            selected_end: to,
          }
      : from
        ? { selected_single: from }
        : {};

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex w-full items-center gap-2 rounded-(--radius-md) border bg-(--surface-1) px-3 py-2 text-left text-sm transition-colors ${
          open
            ? "border-(--brand-primary)"
            : "border-(--border-subtle) hover:border-(--brand-primary)"
        }`}
      >
        <Calendar
          size={15}
          strokeWidth={1.8}
          className="shrink-0 text-(--text-secondary)"
        />
        <span
          className={`flex-1 ${label ? "text-(--text-primary)" : "text-(--text-muted)"}`}
        >
          {label ?? placeholder}
        </span>
        {label && (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            aria-label="Clear dates"
            className="flex items-center text-(--text-secondary) hover:text-(--text-primary)"
          >
            <X size={15} strokeWidth={2} />
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute left-0 z-30 mt-2 w-[320px] overflow-hidden rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-1) shadow-[var(--shadow-popover)]"
        >
          <div className="flex items-center justify-between border-b border-(--border-subtle) px-3 py-2">
            <button
              type="button"
              onClick={() => setVisibleMonth((m) => subMonths(m, 1))}
              aria-label="Previous month"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)"
            >
              <ChevronLeft size={15} strokeWidth={2} />
            </button>
            <p className="text-[13px] font-bold text-(--text-primary)">
              {format(visibleMonth, "MMMM yyyy")}
            </p>
            <button
              type="button"
              onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
              aria-label="Next month"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)"
            >
              <ChevronRight size={15} strokeWidth={2} />
            </button>
          </div>

          <DayPicker
            fixedWeeks
            showOutsideDays
            hideNavigation
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            onDayClick={handleDayClick}
            modifiers={modifiers}
            modifiersClassNames={modifierClassNames}
            classNames={dayPickerClassNames}
          />

          <div className="flex items-center justify-between border-t border-(--border-subtle) px-3 py-2 text-[12px]">
            <span className="text-(--text-muted)">
              {anchor ? "Pick the end date" : "Pick the start date"}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-(--brand-primary) hover:underline"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
